import React from 'react';
import { Menu } from 'lucide-react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../components/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useUnreadCount } from '../../lib/chat/hooks/useUnreadCount';

interface FeedTopBarProps {
  /** Only used on the feed home page to switch feed type */
  onFeedTabChange?: (tab: string) => void;
}

const tabs = [
  { key: 'home', label: 'For You', route: '/' },
  { key: 'items', label: 'Items', route: '/marketplace' },
  { key: 'providers', label: 'Services', route: '/providers' },
];

export const FeedTopBar: React.FC<FeedTopBarProps> = ({ onFeedTabChange }) => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { unreadCount: notifUnread } = useNotifications();
  const chatUnread = useUnreadCount(user?.id || '');
  const hasUnread = (notifUnread + chatUnread) > 0;

  const openSideMenu = () => {
    window.dispatchEvent(new CustomEvent('open-side-menu'));
  };

  // Determine active tab from current route
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/marketplace' || path === '/shop') return 'items';
    if (path === '/providers' || path === '/services') return 'providers';
    return 'home';
  };

  const activeTab = getActiveTab();

  const handleTabClick = (tab: typeof tabs[number]) => {
    if (tab.key === 'home' && location.pathname === '/') {
      // Already on home — just switch feed type if handler provided
      onFeedTabChange?.('all');
      return;
    }
    history.push(tab.route);
  };

  return (
    <>
      <div className="feed-top-bar fixed top-0 left-0 right-0 z-50 safe-area-top">
        <div className="top-gradient pt-3 pb-6">
          <div className="flex items-center justify-between px-4">
            {/* Logo + App Name */}
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/logo.svg"
                alt="Warka"
                className="w-7 h-7 drop-shadow-lg"
              />
              <span
                className="font-bold text-lg"
                style={{ color: 'var(--feed-overlay-text)' }}
              >
                {t('appName')}
              </span>
            </Link>

            {/* Center tabs */}
            <div className="flex items-center gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabClick(tab)}
                  className={`relative px-3 py-2 text-sm font-semibold transition-all duration-300`}
                  style={{ color: activeTab === tab.key ? 'var(--feed-overlay-text)' : 'var(--feed-overlay-text-muted)' }}
                >
                  {tab.label}
                  {activeTab === tab.key && (
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-[3px] rounded-full tab-indicator" style={{ backgroundColor: 'var(--feed-overlay-text)' }} />
                  )}
                </button>
              ))}
            </div>

            {/* Hamburger menu button */}
            <button
              onClick={openSideMenu}
              className="relative w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 active:scale-90"
            >
              <Menu className="w-5 h-5" style={{ color: 'var(--feed-overlay-text)' }} />
              {hasUnread && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-black/30" />
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
